import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search, Leaf, Flame, Phone, X, Info, Minus, Plus, ShoppingBag, Heart } from "lucide-react";
import { toast } from "sonner";
import { MENU_CATEGORIES } from "../data/menu";
import { CATEGORIES_PL, DISHES_PL } from "../data/menuPl";
import { SITE } from "../data/site";
import { IMG, dishImg } from "../data/images";
import { Reveal, Overline } from "../components/Reveal";
import { FoodImg } from "../components/FoodImg";
import { useCart } from "../components/Cart";
import { useLang } from "../i18n";

const VegDot = ({ veg }) => (
    <span
        className={`inline-block w-3.5 h-3.5 rounded-[4px] border-2 ${veg ? "border-emerald-500" : "border-red-700"} relative shrink-0`}
        title={veg ? "Vegetarian" : "Non-vegetarian"}
        aria-label={veg ? "Vegetarian" : "Non-vegetarian"}
    >
        <span className={`absolute inset-[2.5px] rounded-full ${veg ? "bg-emerald-500" : "bg-red-700"}`} />
    </span>
);

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const PopularBadge = ({ className = "" }) => {
    const { t } = useLang();
    return (
    <span
        className={`inline-flex items-center gap-1 rounded-full bg-saffron px-2 py-0.5 text-[9px] font-bold tracking-[0.12em] uppercase text-ink ${className}`}
    >
        <Heart className="w-2.5 h-2.5 fill-ink" /> {t("misc.loved")}
    </span>
    );
};

export default function MenuPage() {
    const [query, setQuery] = useState("");
    const [activeCat, setActiveCat] = useState("all");
    const [vegOnly, setVegOnly] = useState(false);
    const [dish, setDish] = useState(null);
    const [qty, setQty] = useState(1);
    const cart = useCart();
    const { lang, t } = useLang();

    const dName = (d) => (lang === "pl" && DISHES_PL[d.name]?.name) || d.name;
    const dDesc = (d) => (lang === "pl" && DISHES_PL[d.name]?.desc) || d.desc;
    const cTitle = (c) => (lang === "pl" && CATEGORIES_PL[c.id]?.title) || c.title;
    const cDesc = (c) => (lang === "pl" && CATEGORIES_PL[c.id]?.desc) || c.desc;

    const categories = useMemo(() => {
        const q = query.trim().toLowerCase();
        return MENU_CATEGORIES.map((cat, idx) => ({
            ...cat,
            num: idx + 1,
            dishes: cat.dishes.filter((d) => {
                const pl = DISHES_PL[d.name];
                return (
                    (!vegOnly || d.veg) &&
                    (!q ||
                        d.name.toLowerCase().includes(q) ||
                        d.desc.toLowerCase().includes(q) ||
                        (pl &&
                            ((pl.name && pl.name.toLowerCase().includes(q)) ||
                                (pl.desc && pl.desc.toLowerCase().includes(q)))))
                );
            }),
        })).filter((cat) => (activeCat === "all" || cat.id === activeCat) && cat.dishes.length > 0);
    }, [query, activeCat, vegOnly]);

    const openDish = (d, catTitle, catDesc) => {
        setDish({ ...d, category: catTitle, catDesc });
        setQty(1);
    };

    const addToCart = () => {
        cart?.addItem(dish, qty);
        toast.success(`${qty} × ${dName(dish)} ${t("cart.toast")}`, {
            style: { background: "#1A1A20", border: "1px solid rgba(197,160,89,0.3)", color: "#FDFBF7" },
        });
        setDish(null);
    };

    return (
        <main data-testid="menu-page" className="min-h-screen">
            <section className="relative pt-36 pb-16 sm:pt-44 sm:pb-20 overflow-hidden">
                <div className="absolute inset-0">
                    <img src={IMG.biryani} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
                </div>
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <Overline>{t("menu.overline")}</Overline>
                        <h1 className="mt-4 font-serif text-4xl sm:text-6xl lg:text-7xl text-cream leading-[1.03]">
                            {t("menu.h1a")} <span className="italic gold-text">{t("menu.h1b")}</span>
                        </h1>
                        <p className="mt-5 text-stone-400 font-light max-w-xl">
                            {t("menu.intro")}
                        </p>
                    </Reveal>

                    <Reveal delay={0.15} className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                            <input
                                data-testid="menu-search-input"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={t("menu.search")}
                                className="w-full rounded-full bg-elevated/70 gold-border pl-11 pr-4 py-3.5 text-base sm:text-sm text-cream placeholder:text-stone-500 focus:outline-none focus:border-saffron/60 transition-colors duration-300"
                            />
                        </div>
                        <button
                            data-testid="menu-veg-toggle"
                            onClick={() => setVegOnly((v) => !v)}
                            aria-pressed={vegOnly}
                            className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-xs font-semibold tracking-[0.12em] uppercase border transition-[background-color,border-color,color] duration-300 ${
                                vegOnly
                                    ? "bg-emerald-600/90 border-emerald-500 text-cream"
                                    : "gold-border text-stone-300 hover:border-emerald-500/50"
                            }`}
                        >
                            <Leaf className="w-4 h-4" /> {t("menu.vegOnly")}
                        </button>
                        <button
                            data-testid="menu-view-cart-button"
                            onClick={() => cart?.openCart()}
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-saffron px-6 py-3.5 text-xs font-semibold tracking-[0.12em] uppercase text-ink hover:bg-saffron-bright transition-colors duration-300"
                        >
                            <ShoppingBag className="w-4 h-4" /> {t("menu.viewOrder")} · {cart?.count ?? 0}
                        </button>
                    </Reveal>

                    <Reveal delay={0.2} className="mt-8 flex gap-2.5 overflow-x-auto hide-scrollbar pb-2 -mx-5 px-5 sm:mx-0 sm:px-0">
                        <button
                            data-testid="menu-category-tab-all"
                            onClick={() => setActiveCat("all")}
                            className={`shrink-0 rounded-full px-5 py-2.5 text-xs tracking-[0.12em] uppercase border transition-[background-color,color,border-color] duration-300 ${
                                activeCat === "all"
                                    ? "bg-saffron text-ink border-saffron font-semibold"
                                    : "gold-border text-stone-300 hover:border-saffron/50 hover:text-saffron-glow"
                            }`}
                        >
                            {t("menu.all")}
                        </button>
                        {MENU_CATEGORIES.map((c) => (
                            <button
                                key={c.id}
                                data-testid={`menu-category-tab-${c.id}`}
                                onClick={() => setActiveCat(c.id)}
                                className={`shrink-0 rounded-full px-5 py-2.5 text-xs tracking-[0.12em] uppercase border transition-[background-color,color,border-color] duration-300 ${
                                    activeCat === c.id
                                        ? "bg-saffron text-ink border-saffron font-semibold"
                                        : "gold-border text-stone-300 hover:border-saffron/50 hover:text-saffron-glow"
                                }`}
                            >
                                {cTitle(c)}
                            </button>
                        ))}
                    </Reveal>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-32">
                {categories.length === 0 && (
                    <p data-testid="menu-no-results" className="text-center text-stone-400 py-20 font-serif italic text-2xl">
                        {t("menu.noResults")}
                    </p>
                )}
                {categories.map((cat) => (
                    <Reveal key={cat.id} className="mt-14 first:mt-0">
                        <div data-testid={`menu-category-${cat.id}`} style={{ contentVisibility: "auto", containIntrinsicSize: "auto 480px" }}>
                            <div className="flex items-baseline gap-4 flex-wrap">
                                <span className="font-cinzel text-sm text-brass tracking-[0.3em]">
                                    {String(cat.num).padStart(2, "0")}
                                </span>
                                <h2 className="font-serif text-3xl sm:text-4xl text-cream">{cTitle(cat)}</h2>
                                <span className="text-xs text-stone-500 font-light">{cDesc(cat)}</span>
                            </div>
                            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                                {cat.dishes.map((d, i) => (
                                    <motion.button
                                        key={d.name}
                                        data-testid={`menu-dish-${cat.id}-${i}`}
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.45, delay: 0.03 * i }}
                                        onClick={() => openDish(d, cTitle(cat), cDesc(cat))}
                                        className="group text-left gold-border rounded-xl bg-elevated/50 p-3.5 hover:border-saffron/50 hover:bg-elevated transition-[border-color,background-color] duration-400 flex gap-4"
                                    >
                                        <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0 gold-border relative">
                                            <FoodImg
                                                src={dishImg(d, 300)}
                                                alt={d.name}
                                                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                                            />
                                            {d.popular && (
                                                <span data-testid={`popular-badge-${slug(d.name)}`} className="absolute bottom-1 left-1">
                                                    <PopularBadge />
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className="font-serif text-lg text-cream group-hover:text-saffron-glow transition-colors duration-300 leading-snug">
                                                    {dName(d)}
                                                </p>
                                                <VegDot veg={d.veg} />
                                            </div>
                                            {dDesc(d) ? (
                                                <p className="mt-1 text-xs text-stone-400 font-light leading-relaxed line-clamp-2">
                                                    {dDesc(d)}</p>
                                            ) : null}
                                            <div className="mt-2 flex items-center justify-between">
                                                <span className="text-sm font-semibold text-saffron-glow">{d.price} zł</span>
                                                {d.spicy >= 2 && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase text-saffron-deep">
                                                        <Flame className="w-3 h-3" /> Spicy
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </motion.button>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                ))}

                <Reveal className="mt-16">
                    <div className="gold-border rounded-2xl bg-onyx/70 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                        <Info className="w-6 h-6 text-brass shrink-0" strokeWidth={1.5} />
                        <p className="text-sm text-stone-400 font-light leading-relaxed flex-1">
                            {t("menu.allergen")}
                        </p>
                        <a
                            data-testid="menu-footer-call-button"
                            href={SITE.phoneHref}
                            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-saffron px-6 py-3 text-xs font-semibold tracking-[0.12em] uppercase text-ink hover:bg-saffron-bright transition-colors duration-300"
                        >
                            <Phone className="w-4 h-4" /> {t("menu.callToOrder")}
                        </a>
                    </div>
                </Reveal>
            </section>

            <AnimatePresence>
                {dish && (
                    <motion.div
                        data-testid="menu-dish-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[90] bg-ink/90 backdrop-blur-lg flex items-end sm:items-center justify-center p-0 sm:p-6"
                        onClick={() => setDish(null)}
                        role="dialog"
                        aria-label={dish.name}
                    >
                        <motion.div
                            initial={{ y: 60, opacity: 0, scale: 0.98 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            exit={{ y: 40, opacity: 0 }}
                            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full sm:max-w-md gold-border bg-onyx rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92svh] overflow-y-auto"
                        >
                            <div className="relative aspect-[16/10] overflow-hidden group">
                                <FoodImg
                                    src={dishImg(dish, 900)}
                                    alt={dish.name}
                                    className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-onyx via-transparent to-transparent" />
                                <button
                                    data-testid="menu-dish-modal-close"
                                    onClick={() => setDish(null)}
                                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-ink/70 backdrop-blur gold-border flex items-center justify-center hover:border-saffron/60 transition-colors duration-300"
                                    aria-label="Close"
                                >
                                    <X className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                                <div className="absolute bottom-4 left-6 flex items-center gap-2.5">
                                    <span
                                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] tracking-[0.15em] uppercase border bg-ink/70 backdrop-blur ${
                                            dish.veg ? "border-emerald-500/50 text-emerald-400" : "border-red-700/60 text-red-400"
                                        }`}
                                    >
                                        <VegDot veg={dish.veg} /> {dish.veg ? t("menu.veg") : t("menu.nonveg")}
                                    </span>
                                    {dish.spicy >= 2 && (
                                        <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] tracking-[0.15em] uppercase border border-saffron-deep/50 text-saffron-deep bg-ink/70 backdrop-blur">
                                            <Flame className="w-3 h-3" /> {t("menu.spicy")}
                                        </span>
                                    )}
                                    {dish.popular && (
                                        <span data-testid="menu-dish-modal-popular">
                                            <PopularBadge />
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="p-6 sm:p-8">
                                <p className="text-[10px] tracking-[0.3em] uppercase text-brass">{dish.category}</p>
                                <div className="mt-2 flex items-start justify-between gap-4">
                                    <h3 className="font-serif text-3xl text-cream leading-tight">{dName(dish)}</h3>
                                    <span data-testid="menu-dish-modal-price" className="font-serif text-2xl text-saffron-glow shrink-0">
                                        {dish.price} zł
                                    </span>
                                </div>
                                <p className="mt-3 text-sm text-stone-300 font-light leading-relaxed">{dDesc(dish) || dish.catDesc}</p>

                                <div className="mt-6 flex items-center gap-4">
                                    <div className="flex items-center gap-3 gold-border rounded-full px-2 py-1.5">
                                        <button
                                            data-testid="menu-dish-qty-minus"
                                            onClick={() => setQty((q) => Math.max(1, q - 1))}
                                            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-elevated active:bg-elevated transition-colors duration-200"
                                            aria-label="Decrease quantity"
                                        >
                                            <Minus className="w-4 h-4" />
                                        </button>
                                        <span data-testid="menu-dish-qty" className="w-6 text-center font-semibold">{qty}</span>
                                        <button
                                            data-testid="menu-dish-qty-plus"
                                            onClick={() => setQty((q) => Math.min(20, q + 1))}
                                            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-elevated active:bg-elevated transition-colors duration-200"
                                            aria-label="Increase quantity"
                                        >
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <motion.button
                                        data-testid="menu-dish-add-to-cart"
                                        onClick={addToCart}
                                        whileTap={{ scale: 0.97 }}
                                        className="flex-1 flex items-center justify-center gap-2.5 rounded-full bg-saffron px-6 py-3.5 text-sm font-semibold tracking-wide text-ink hover:bg-saffron-bright hover:shadow-[0_0_30px_rgba(217,119,6,0.3)] transition-[background-color,box-shadow] duration-300"
                                    >
                                        <ShoppingBag className="w-4 h-4" />
                                        {t("menu.add")} · {dish.price * qty} zł
                                    </motion.button>
                                </div>
                                <p className="mt-4 text-[11px] text-stone-500">
                                    {t("menu.note")}
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </main>
    );
}
